// @flow
import React from "react";
import InputGroup from "react-bootstrap/lib/InputGroup";
import FormControl from "react-bootstrap/lib/FormControl";
import Icons from "@hs/icons";
import loc from "i18next";
import Button from "react-bootstrap/lib/Button";
import LocatorButton from "components/LocatorButton";
import { ParseLocation, LocationToString } from "components/MapViewer/utils";
import {
    type Location,
    type MapEditLocationResult,
    type MarkerDTO,
} from "data/types";
import { openMapModal } from "components/MapModal";
import styles from "./GeoPointInput.module.css";
import GeoPlaceInput from "components/GeoPlaceInput";
import {
    SupportGeoPlaceInput,
    SupportGeoPointEdit,
    SupportGeoRadiusEdit,
    getGeoPointProvider,
    getGeoRadiusProvider,
} from "components/MapViewer/providerSupport";

type Props = {
    /** displayName of field */
    label: ?string,
    /** current location in string format */
    value: ?string,
    /** whether to show an icon before the text field */
    showIcon: boolean,
    /** callback when user sets location
     * @param {string} value new location in string format
     */
    onChange: (value: string) => void,
    /** optional callback when user wants to manually set location on map
     * if defined this needs to take of calling openMapModal!
     */
    onSetLocationManually?: () => void,
    /** callback when user sets location by map
     * only needed if onSetLocationManually is not defined
     * @param {MapEditLocationResult} editLocationResult location set
     */
    onManualLocation?: (editLocationResult: MapEditLocationResult) => void,
    /** callback when user wants to triangulate - when not set then
     * button not visible
     */
    onTriangulate?: () => void,
    /** circle radius */
    radius?: number,
    /** optional CSS class to apply to root element */
    className?: string,
    /** placeholder text to display when @see {value} is empty */
    placeholder?: string,
    /** optional (custom) location marker icon */
    marker?: MarkerDTO,
};

type State = {
    value: ?string,
};

class GeoPointInput extends React.PureComponent<Props, State> {
    static defaultProps = {
        showIcon: true,
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            value: props.value,
        };
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.value !== this.props.value) {
            this.setState({ value: this.props.value });
        }
    }

    /** change location input text value */
    changeValue = (e) =>
        this.setState({ value: e.target.value }, () =>
            this.props.onChange(this.state.value)
        );

    changeValueFromPlace = (value: string) =>
        this.changeValue({ target: { value } });

    /** retrieve location from current GPS position */
    _setCurrentLocation = (location: Location): void =>
        this.setState({ value: LocationToString(location) }, () =>
            this.props.onChange(this.state.value)
        );

    /** show map and allow user to change marker's location */
    _setLocationManually = () => {
        // if (this.state.value.length === 0)
        //     this._setCurrentLocation({ longitude: 0, latitude: 0 });
        openMapModal({
            mapProvider:
                this.props.radius == null
                    ? getGeoPointProvider()
                    : getGeoRadiusProvider(),
            useMarker: true,
            radius: this.props.radius,
            locations: [
                Object.assign(
                    {},
                    ParseLocation(
                        this.state.value != null &&
                            String(this.state.value).length > 0
                            ? this.state.value
                            : "0,0"
                    ),
                    {
                        label: /* istanbul ignore next*/ this.props.label || "",
                        icon:
                            /* istanbul ignore next*/ this.props.radius == null
                                ? this.props.marker
                                : undefined,
                    }
                ),
            ],
            // onSet: this._setCurrentLocation
            onSet: (editLocationResult: MapEditLocationResult) =>
                this.props.onManualLocation(editLocationResult),
        });
    };

    _supportGeoPointEdit = () =>
        this.props.radius == null
            ? SupportGeoPointEdit()
            : SupportGeoRadiusEdit();

    render = () => (
        <InputGroup className={styles.inputGroup}>
            {this.props.showIcon && (
                <InputGroup.Addon className={styles.iconWrapper}>
                    <Icons.Library name="location-dot" fixedWidth />
                </InputGroup.Addon>
            )}
            {SupportGeoPlaceInput() && (
                <GeoPlaceInput
                    placeholder={
                        this.props.placeholder ||
                        loc.t("common:location.notSet")
                    }
                    value={/* istanbul ignore next */ this.state.value || ""}
                    style={{
                        flexGrow: 1,
                    }}
                    onChange={this.changeValueFromPlace}
                    id={this.props.id}
                />
            )}
            {!SupportGeoPlaceInput() && (
                <FormControl
                    type="text"
                    value={this.state.value || ""}
                    onChange={this.changeValue}
                    placeholder={loc.t("common:location.notSet")}
                    id={this.props.id}
                />
            )}
            <InputGroup.Addon className={styles.buttonsWrapper}>
                {this.props.onTriangulate && (
                    <Button
                        title={loc.t("common:location.setTriangulation")}
                        onClick={this.props.onTriangulate}
                        className={styles.button}
                    >
                        <Icons.Library fixedWidth name="camera" />
                    </Button>
                )}
                <LocatorButton
                    onSetLocation={this._setCurrentLocation}
                    className={styles.button}
                />
                {this._supportGeoPointEdit() && (
                    <Button
                        title={loc.t("common:location.setManual")}
                        onClick={
                            this.props.onSetLocationManually ||
                            this._setLocationManually
                        }
                        className={styles.button}
                    >
                        <Icons.Library fixedWidth name="map-pin" />
                    </Button>
                )}
            </InputGroup.Addon>
            {this.props.children}
        </InputGroup>
    );
}
export default GeoPointInput;
